// Copyright 2026 The Simlin Authors. All rights reserved.
// Use of this source code is governed by the Apache License,
// Version 2.0, that can be found in the LICENSE file.

// D7.3: the one-time, branded welcome shown to a newly onboarded user. It
// orients them in a sentence and routes into the guided first project (the New
// Project flow, which offers the "start from a template" picker). Copy is kept
// minimal and brand-aligned so Williams can edit it without restructuring.

import * as React from 'react';

import { Link } from 'wouter';

import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@simlin/diagram';

import styles from './WelcomeDialog.module.css';

interface WelcomeDialogProps {
  open: boolean;
  // First name for the greeting; '' falls back to a generic welcome.
  userName: string;
  // Dismiss without starting a project ("Explore on my own" / backdrop / esc).
  onDismiss: () => void;
  // Primary action: mark seen + close. Navigation to /new is handled by the Link.
  onStart: () => void;
}

export const WelcomeDialog: React.FC<WelcomeDialogProps> = ({ open, userName, onDismiss, onStart }) => {
  const greeting = userName ? `Welcome, ${userName}!` : 'Welcome to Systems Analyzer!';
  return (
    <Dialog open={open} onClose={onDismiss} className={styles.dialog}>
      <DialogTitle>{greeting}</DialogTitle>
      <DialogContent>
        <p className={styles.lead}>Understand systems. Drive change. Create coherence.</p>
        <p className={styles.body}>
          Systems Analyzer helps you build, simulate, and debug your intuition about complex systems. The fastest
          way to start is from a curated template &mdash; pick one and adapt it as you go.
        </p>
      </DialogContent>
      <DialogActions>
        <Button onClick={onDismiss} color="primary">
          Explore on my own
        </Button>
        <Link to="/new" onClick={onStart} className={styles.startLink}>
          <Button variant="contained" color="primary">
            Create your first project
          </Button>
        </Link>
      </DialogActions>
    </Dialog>
  );
};
